import { ArrowRight, CheckCircle2, Clock3, Inbox } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { Badge, Section, WorkspaceSkeleton } from '../components/UI'
import { useTeamScope } from '../components/TeamScope'
import {
  formatDuration,
  formatRatio,
  formatRelativeTime,
  loadTodayOperations,
  percentMet,
  shortThreadId,
  type DailyActivity,
  type DailyBenchmarkSummary,
  type DailyOperatingPressure,
  type DailyQueueMetrics,
  type HourPoint,
  type QueuePressure,
  type TeamPerson,
  type WorkRecord
} from '../lib/liveOperations'

type State = {
  records: WorkRecord[]
  daily: DailyActivity | null
  queueMetrics: DailyQueueMetrics | null
  benchmarkSummary: DailyBenchmarkSummary | null
  queuePressure: QueuePressure | null
  operatingPressure: DailyOperatingPressure | null
  people: TeamPerson[]
  hourly: HourPoint[]
}

export function Dashboard() {
  const { selectedTeamId } = useTeamScope()
  const [data, setData] = useState<State>({
    records: [],
    daily: null,
    queueMetrics: null,
    benchmarkSummary: null,
    queuePressure: null,
    operatingPressure: null,
    people: [],
    hourly: []
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    setError('')
    loadTodayOperations(selectedTeamId)
      .then(result => setData({
        records: result.records,
        daily: result.daily,
        queueMetrics: result.queueMetrics,
        benchmarkSummary: result.benchmarkSummary,
        queuePressure: result.queuePressure,
        operatingPressure: result.operatingPressure,
        people: result.people,
        hourly: result.hourly
      }))
      .catch(err => setError(err instanceof Error ? err.message : 'Could not load live operations.'))
      .finally(() => setLoading(false))
  }, [selectedTeamId])

  const replied = data.daily?.threads_replied ?? data.records.filter(r => r.work_status === 'replied').length
  const open = data.records.length - replied
  const max = Math.max(1, ...data.hourly.map(h => Math.max(h.opened, h.replied)))

  const responseBenchmark = percentMet(
    data.benchmarkSummary?.reply_sent_target_met_count ?? 0,
    data.benchmarkSummary?.reply_sent_target_evaluated_count ?? 0
  )

  const oldestOpen = useMemo(
    () => data.records
      .filter(record => record.work_status !== 'replied')
      .sort((a, b) =>
        new Date(a.inbound_received_at ?? a.first_opened_at ?? a.last_activity_at).getTime() -
        new Date(b.inbound_received_at ?? b.first_opened_at ?? b.last_activity_at).getTime()
      )[0],
    [data.records]
  )


  if (loading) return <WorkspaceSkeleton rows={4}/>

  return <div className="dashboard stack-lg">
    {error && <div className="notice notice-inline"><strong>Live data unavailable:</strong><span>{error}</span></div>}

    <section className="dashboard-snapshot" aria-label="Today's live operational snapshot">
      <div className="snapshot-item">
        <span>Waiting now</span>
        <strong>{data.queuePressure?.waiting_threads ?? 0}</strong>
        <small>{data.queuePressure?.waiting_over_30m ?? 0} waiting over 30m</small>
      </div>

      <div className="snapshot-item">
        <span>Customer emails per active employee</span>
        <strong>{data.operatingPressure?.inbound_threads_per_active_person ?? '—'}</strong>
        <small>{data.operatingPressure?.inbound_threads ?? 0} customer emails received today</small>
      </div>

      <div className="snapshot-item">
        <span>Response SLA met</span>
        <strong>{responseBenchmark == null ? '—' : `${responseBenchmark}%`}</strong>
        <small>Customer email received to first reply sent</small>
      </div>

      <div className="snapshot-item">
        <span>Average response time</span>
        <strong>{formatDuration(data.queueMetrics?.average_arrival_to_reply_sent_seconds)}</strong>
        <small>{open} measured customer email{open === 1 ? '' : 's'} still open</small>
      </div>
    </section>

    <div className="dashboard-primary-grid">
      <Section
        className="flow-panel"
        title="Customer demand and replies"
        subtitle="Customer email demand against measured replies"
        action={<div className="legend"><span><i className="dot receive"/>Received</span><span><i className="dot handled"/>Replied</span></div>}
      >
        <div className="flow-explanation">
          <div>
            <strong>{formatRatio(data.operatingPressure?.reply_output_to_inbound_ratio)}</strong>
            <span>replies sent versus customer emails received</span>
          </div>
          <p>
            This helps you see whether a customer-response gap comes from follow-through, queue pressure or limited team capacity.
          </p>
        </div>

        {data.hourly.length > 0 ? (
          <div className="chart-bars dashboard-chart">
            {data.hourly.map(h =>
              <div className="bar-group" key={h.label} title={`${h.label}: ${h.opened} received, ${h.replied} replied`}>
                <div className="bars">
                  <span className="bar received" style={{height:`${(h.opened / max) * 150}px`}}/>
                  <span className="bar handled" style={{height:`${(h.replied / max) * 150}px`}}/>
                </div>
                <small>{h.label.replace(':00','')}</small>
              </div>
            )}
          </div>
        ) : <p className="muted">No provider mailbox activity has been measured yet today.</p>}
      </Section>

      <Section
        title="What needs attention"
        subtitle="Waiting customer emails, follow-through and workload context"
      >
        <div className="attention-list">
          {(data.queuePressure?.waiting_threads ?? 0) > 0 ? (
            <Link to="/app/queue" className="attention-item attention-warn">
              <div className="attention-icon"><Inbox size={18}/></div>
              <div>
                <strong>{data.queuePressure?.waiting_threads} customer email{data.queuePressure?.waiting_threads === 1 ? '' : 's'} waiting</strong>
                <span>{data.queuePressure?.waiting_over_60m ?? 0} have waited over 60 minutes</span>
              </div>
              <b>{formatDuration(data.queuePressure?.oldest_current_wait_seconds)} oldest</b>
            </Link>
          ) : (
            <div className="attention-item attention-good">
              <div className="attention-icon"><CheckCircle2 size={18}/></div>
              <div><strong>No customer email backlog detected</strong><span>No tracked customer email is currently waiting on a newer customer message.</span></div>
              <b>Clear</b>
            </div>
          )}

          {oldestOpen && (
            <Link to="/app/queue" className="attention-item">
              <div className="attention-icon"><Clock3 size={18}/></div>
              <div>
                <strong>Oldest open customer email</strong>
                <span>Email reference {shortThreadId(oldestOpen.external_thread_id)}</span>
              </div>
              <b>{formatRelativeTime(oldestOpen.inbound_received_at ?? oldestOpen.first_opened_at)}</b>
            </Link>
          )}

        </div>

        <div className="attention-actions">
          <Link className="panel-link" to="/app/reports">
            Review customer-email performance <ArrowRight size={14}/>
          </Link>
          <Link className="panel-link" to="/app/queue">
            Open live queue <ArrowRight size={14}/>
          </Link>
        </div>
      </Section>
    </div>

    <Section
      className="team-pulse-panel"
      title="Team accountability"
      subtitle="See customer-email workload and follow-through without reducing people to a ranking"
      action={<Link className="text-link" to="/app/team">Open team</Link>}
    >
      <div className="team-pulse-list team-pulse-grid">
        {data.people.slice(0,4).map(person =>
          <Link to={`/app/team/${person.userId}`} className="team-pulse-row" key={person.userId}>
            <div className="person">
              <div className="avatar small">{person.initials}</div>
              <div><strong>{person.displayName}</strong><small>{person.teamName}</small></div>
            </div>

            <div className="team-pulse-work">
              <span>{person.replied} replied</span>
              <small>{person.open} open · {formatDuration(person.averageQueueWaitSeconds)} avg response</small>
            </div>

            <Badge tone={person.status === 'recent' ? 'good' : 'neutral'}>
              {person.status === 'recent' ? 'Recent data' : person.status === 'earlier' ? 'Earlier data' : 'No data'}
            </Badge>
          </Link>
        )}

        {data.people.length === 0 && <p className="muted">No team activity has been measured yet today.</p>}
      </div>
    </Section>
  </div>
}
